import { TransactionResponse } from '@ethersproject/providers'
import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useActiveWeb3React } from '../../hooks'
import { AppDispatch, AppState } from '../index'
import { addTransaction } from './actions'
import { TransactionDetails } from './reducer'

const APPROVAL_PENDING_GRACE_PERIOD = 30 * 60 * 1000

export function sameAddress(left?: string, right?: string): boolean {
  return !!left && !!right && left.toLowerCase() === right.toLowerCase()
}

export function isTransactionFromAccount(transaction: TransactionDetails, account?: string): boolean {
  return sameAddress(transaction.from, account)
}

// helper that can take a ethers library transaction response and add it to the list of transactions
export function useTransactionAdder(): (
  response: TransactionResponse,
  customData?: { summary?: string; approval?: { tokenAddress: string; spender: string } }
) => void {
  const { chainId, account } = useActiveWeb3React()
  const dispatch = useDispatch<AppDispatch>()

  return useCallback(
    (
      response: TransactionResponse,
      { summary, approval }: { summary?: string; approval?: { tokenAddress: string; spender: string } } = {}
    ) => {
      if (!account) return
      if (!chainId) return

      const { hash } = response
      if (!hash) {
        throw Error('No transaction hash found.')
      }
      dispatch(addTransaction({ hash, from: account, chainId, approval, summary }))
    },
    [dispatch, chainId, account]
  )
}

// returns all the transactions for the current chain
export function useAllTransactions(): { [txHash: string]: TransactionDetails } {
  const { chainId } = useActiveWeb3React()

  const state = useSelector<AppState, AppState['transactions']>(state => state.transactions)

  return chainId ? state[chainId] ?? {} : {}
}

export function useIsTransactionPending(transactionHash?: string): boolean {
  const transactions = useAllTransactions()

  if (!transactionHash || !transactions[transactionHash]) return false

  return !transactions[transactionHash].receipt
}

// returns whether a token has a pending approval transaction
export function useHasPendingApproval(tokenAddress: string | undefined, spender: string | undefined): boolean {
  const { account } = useActiveWeb3React()
  const allTransactions = useAllTransactions()
  return useMemo(
    () =>
      typeof tokenAddress === 'string' &&
      typeof spender === 'string' &&
      typeof account === 'string' &&
      Object.keys(allTransactions).some(hash => {
        const transaction = allTransactions[hash]
        if (transaction?.receipt) {
          return false
        }

        if (!isTransactionFromAccount(transaction, account)) {
          return false
        }

        if (new Date().getTime() - transaction.addedTime > APPROVAL_PENDING_GRACE_PERIOD) {
          return false
        }

        return sameAddress(transaction.approval?.tokenAddress, tokenAddress) && sameAddress(transaction.approval?.spender, spender)
      }),
    [account, allTransactions, spender, tokenAddress]
  )
}
